import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import Header from "../../header";
import Navbar from "../../navbar";
import iconMenuDots from "../../../assets/media/icon-menu-dots.svg";
import moment from "moment";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import iconSearch from "../../../assets/media/icon-search.svg";
import { ReactComponent as ArrowLeft } from "../../../assets/svgicons/ArrowLeft.svg";
import {
  fetchCompanyById,
} from "../../../store/reducers/companiesSlice.js";
import { fetchApplications } from "../../../store/reducers/applicationSlice.js";
import "./styles.css";
import Pagination from "../dashboard/appPagination.js";
import userImagePlaceholder from "../../../assets/media/avatar2.jpg";

const columnHelper = createColumnHelper();

export const formatDate = (inputDate) => {
  const date = new Date(inputDate);
  const options = { year: "numeric", month: "long", day: "2-digit" };
  return date.toLocaleDateString(undefined, options);
};


const stateButtonMap = {
  in_process: { className: "status-inprocess", text: "In Process", state: "in_process" },
  pending: { className: "status-pending", text: "Pending", state: "pending" },
  approved: { className: "status-approved", text: "Approved", state: "approved" },
  rejected: { className: "status-rejected", text: "Rejected", state: "rejected" },
  discrepancies: { className: "status-discrepancies", text: "Discrepancies", state: "discrepancies" },
  drafts: { className: "status-drafts", text: "Drafts", state: "drafts" },
  renewal: { className: "status-renewal", text: "Renewal", state: "renewal" },
  dicrepancy_application_assigned_to_comapany_user: { className: "status-discrepancies-assigned-to-company", text: "Discrepancies Assigned to Company", state: "dicrepancy_application_assigned_to_comapany_user" },
  discrepancy_application_assigned_to_employee_user: { className: "status-discrepancies-assigned-to-employee", text: "Discrepancies Assigned to Employee", state: "discrepancy_application_assigned_to_employee_user" },
  application_sent_to_discrepancy_by_company: { className: "status-application-sent-to-discrepancy-by-company", text: "Application Sent to Discrepancy by Company", state: "application_sent_to_discrepancy_by_company" },
  application_submitted_by_company_user_to_company: { className: "status-application-submitted-by-company-user-to-company", text: "Application Submitted by Company User to Company", state: "application_submitted_by_company_user_to_company" },
  descrepency_GB_GB: { className: "status-descrepency-GB-GB", text: "Descrepency GB GB", state: "descrepency_GB_GB" },
}

// Mapping between checkbox labels and API state values
const statusFilterMap = {
  "All": null,
  "Approved": "approved",
  "Rejected": "rejected",
  "In Process": "in_process",
  "Discrepancies": "discrepancies",
  "Renewal": "renewal",
  "Discrepancies Assigned to Company": "dicrepancy_application_assigned_to_comapany_user",
  "Discrepancies Assigned to Employee": "discrepancy_application_assigned_to_employee_user",
  "Drafts": "drafts",
}

const CompanyDetails = () => {
  const initialState = {
    All: true,
    Approved: false,
    Rejected: false,
    "In Process": false,
  };

  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [checkboxes, setCheckboxes] = useState(initialState);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const companies = useSelector((state) => state.companies);
  const {
    applications: applicationRecords,
    applicationsLoading: applicationsLoader,
  } = useSelector((state) => state.companies);
  const {
    data: applicationsData,
    pagination: applicationsPagination,
    fetchLoading: applicationsFetchLoading,
  } = useSelector((state) => state.applications);


  const companyData = companies?.company;
  const isLoading = companies?.fetchLoading;
  const authToken = user.access_token;
  const { id } = useParams();
  const navigate = useNavigate();

  function CheckboxFilter({ checkboxes, setCheckboxes, setSearchTerm, setCurrentPage }) {
    const handleCheckboxChange = (event) => {
      const { value, checked } = event.target;
      if (value === "All") {
        setCheckboxes((prevCheckboxes) => {
          const newCheckboxes = { ...prevCheckboxes };
          for (const key in newCheckboxes) {
            newCheckboxes[key] = checked;
          }
          return newCheckboxes;
        });
        setSearchTerm("");
        setCurrentPage(1);
      } else {
        setCheckboxes((prevCheckboxes) => ({
          ...prevCheckboxes,
          [value]: checked,
        }));
        setSearchTerm("");
        setCurrentPage(1);
      }
    };

    return (
      <div className="boardFilter">
        {Object.keys(checkboxes).map((checkboxValue) => (
          <div className="form-check form-check-inline" key={checkboxValue}>
            <input
              className="form-check-input"
              type="checkbox"
              value={checkboxValue}
              checked={checkboxes[checkboxValue]}
              onChange={handleCheckboxChange}
            />
            <label className="form-check-label">{checkboxValue}</label>
          </div>
        ))}
      </div>
    );
  }

  const handleInputChange = (event) => {
    filtered(event.target.value);
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to page 1 when searching
  };

  const filtered = useCallback((search = "") => {
    // Use applicationsData from applicationSlice if available, otherwise fallback to applicationRecords
    const sourceData = applicationsData || applicationRecords || [];

    // Handle different response formats
    const applicationsArray = Array.isArray(sourceData)
      ? sourceData
      : sourceData?.applications || sourceData?.data || [];

    // Convert company id to number for comparison
    const companyIdNumber = id ? Number(id) : null;

    const filteredData = applicationsArray
      .filter((item) => {
        // First filter by company_id - only show applications for this company
        if (companyIdNumber) {
          // Check multiple possible field names for company_id
          const itemCompanyId = item.company_id || item.companyId || item.company?.id;
          const itemCompanyIdNumber = itemCompanyId ? Number(itemCompanyId) : null;

          // If company_id doesn't match, exclude this item
          if (itemCompanyIdNumber !== companyIdNumber) {
            return false;
          }
        }

        // If "All" is checked, show all items (that match the company)
        if (checkboxes["All"] === true) {
          return true;
        }

        // Get the application's state value
        const applicationState = item.state;

        // Check each checked checkbox to see if it matches the application state
        for (const checkboxLabel in checkboxes) {
          // Skip "All" and unchecked boxes
          if (checkboxLabel === "All" || checkboxes[checkboxLabel] !== true) {
            continue;
          }

          // Get the state value for this checkbox from statusFilterMap
          const expectedState = statusFilterMap[checkboxLabel];

          // If the state matches, include this item
          if (expectedState && applicationState === expectedState) {
            return true;
          }

          // Also check via stateButtonMap for additional matching
          const stateInfo = stateButtonMap[applicationState];
          if (stateInfo && stateInfo.text === checkboxLabel) {
            return true;
          }
        }

        // If no checkbox matches, exclude this item
        return false;
      })
      .map((item) => ({
        ...item,
        created_at: formatDate(item.created_at),
        updated_at: formatDate(item.updated_at),
        stateText: stateButtonMap[item.state]?.text || item.state,
        applicationId: item.id,
      }))
      .filter((item) => {
        if (!search) return true;
        for (const key in item) {
          if (
            typeof item[key] === "string" &&
            item[key].toLowerCase().includes(search.toLowerCase())
          ) {
            return true;
          }
        }
        return false;
      });

    setData(filteredData);
    return filteredData;
  }, [applicationRecords, applicationsData, checkboxes, id]);
  const getTotalPages = () => {
    // Use client-side calculation based on filtered data
    // Since we're doing client-side filtering, pagination should be based on filtered results
    if (Number.isInteger(data?.length / 10)) return data?.length / 10;
    return Math.floor(data?.length / 10) + 1;
  };
  useEffect(() => {
    dispatch(fetchCompanyById({ accessToken: authToken, companyId: id }));
  }, [authToken, dispatch, id]);

  // Fetch applications by company_id (fetch all for client-side filtering)
  useEffect(() => {
    if (id) {
      // Reset local state when company changes
      setData([]);
      setCurrentPage(1);
      setSearchTerm("");

      const queryParams = {
        company_id: id,
        // Fetch with a large limit to get all applications for client-side filtering
        // If you need server-side pagination, remove this and handle pagination differently
        limit: 1000,
      };
      dispatch(fetchApplications({ accessToken: authToken, queryParams }));
    }
  }, [authToken, dispatch, id]);

  useEffect(() => {
    if (applicationRecords || applicationsData) {
      filtered();
    }
  }, [applicationRecords, applicationsData, checkboxes, filtered]);

  // Paginated data for current page
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * 10;
    const endIndex = startIndex + 10;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage]);

  // React Table columns
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "serial",
        header: "S.NO.",
        cell: (info) => (currentPage - 1) * 10 + info.row.index + 1,
      }),
      columnHelper.display({
        id: "applicationNo",
        header: "Application No.",
        cell: (info) => `GB-${info.row.original.applicationId}`,
      }),
      columnHelper.display({
        id: "applicationName",
        header: "Application Name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardTitle">
              <strong>{row.name}</strong>
              <span>{row.form}</span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "submittedDate",
        header: "Submitted Date",
        cell: (info) => {
          const row = info.row.original;
          return formatDate(row.updated_at);
        },
      }),
      columnHelper.display({
        id: "registeredDate",
        header: "Registered Date",
        cell: (info) => {
          const row = info.row.original;
          return formatDate(row.created_at);
        },
      }),
      columnHelper.display({
        id: "status",
        header: "Status",
        cell: (info) => {
          const row = info?.row?.original;
          if (!row) return <span>—</span>;

          const stateInfo = stateButtonMap[row.state];
          const state = row.state;
          if (state === 'discrepancies') {
            return (
              <span>{stateInfo?.text || row.state}</span>
            );
          }
          return (
            <span>{stateInfo?.text || row.state}</span>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const row = info.row.original;
          return (
            <div className="boardActions">
              <div className="dropdown d-inline-flex">
                <button
                  className="application-dropdown dropdown-toggle border-0 bg-transparent"
                  type="button"
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img src={iconMenuDots} alt="Menu Dots" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="dropdownMenuButton"
                >
                  <li>
                    <Link
                      className="dropdown-item"
                      to={`/view-applications/${row.applicationId}`}
                    >
                      View
                    </Link>
                  </li>
                  <li>
                    <Link
                      className="dropdown-item"
                      to={`/delete-applications/${row.applicationId}`}
                    >
                      Delete
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          );
        },
      }),
    ],
    [currentPage]
  );

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <div>
        <Navbar />
        <Header />
        {isLoading ? (
          <div className="wrapper emp-details-wrapper">
            <div className="d-flex justify-content-center my-4">
              <div
                className=" text-center items-center spinner-border text-primary"
                style={{ width: "4rem", height: "4rem" }}
                role="status"
              ></div>
            </div>
          </div>
        ) : (
          <div className="wrapper emp-details-wrapper">
            <div className="heading-section">
              <div className="heading-wrapper">
                <ArrowLeft className="c-p" onClick={() => navigate(-1)} />
                <p>Registered Company Details</p>
              </div>
              <div className="heading-wrapper" style={{ gap: "20px" }}>
              </div>
            </div>
            {companyData ? (
              <>
                <div className="user-details-section">
                  <div className="user-name">
                    <img src={companyData?.image || userImagePlaceholder} alt="company" />
                    <div className="name-phone">
                      <p>{companyData?.company_name || "—"}</p>
                      <p>{companyData?.company_id || "—"}</p>
                    </div>
                  </div>
                  <div className="user-details">
                    <div className="user-detail-item">
                      <p>Company ID</p>
                      <p>{companyData?.company_id || "—"}</p>
                    </div>
                    <div className="user-detail-item">
                      <p>Company Email</p>
                      <p>{companyData?.company_email || "—"}</p>
                    </div>
                    <div className="user-detail-item">
                      <p>Phone No.</p>
                      <p>{companyData?.phone_no || "—"}</p>
                    </div>
                    <div className="user-detail-item">
                      <p>Number of Employees</p>
                      <p>{companyData?.no_of_employee || "—"}</p>
                    </div>
                    <div className="user-detail-item">
                      <p>Registration Date</p>
                      <p>
                        {companyData?.registration_date
                          ? moment(companyData.registration_date).format("MMMM D, YYYY")
                          : "—"}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-center">Loading company details...</p>
            )}

            <div className="boxAppBoard boxWhite">
              <div className="boardHeader">
                <CheckboxFilter
                  setSearchTerm={setSearchTerm}
                  checkboxes={checkboxes}
                  setCheckboxes={setCheckboxes}
                  setCurrentPage={setCurrentPage}
                />
                <div className="boardHeadRight">
                  <div className="searchForm">
                    <input
                      type="text"
                      placeholder="Search"
                      onChange={handleInputChange}
                      value={searchTerm}
                    />
                    <button>
                      <img src={iconSearch} alt="" />
                    </button>
                  </div>
                  <div className="datePicker"></div>
                </div>
              </div>
              <div className="boardContent">
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <th key={header.id}>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.length > 0 ? (
                        table.getRowModel().rows.map((row) => (
                          <tr key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                              <td key={cell.id}>
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={table.getHeaderGroups()?.[0]?.headers.length}
                            style={{ textAlign: "center", padding: "20px" }}
                          >
                            No applications found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              <Pagination
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={getTotalPages()}
                totalItems={data.length}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default CompanyDetails;

